(() => {
  const contextNode = document.getElementById('GalilCommerceContext');
  if (!contextNode || typeof window.gtag !== 'function') return;

  let context;
  try {
    context = JSON.parse(contextNode.textContent);
  } catch (error) {
    console.warn('Galil analytics context could not be parsed.', error);
    return;
  }

  const state = {
    cart: context.cart || { value_cents: 0, item_count: 0, items: [] },
    checkoutSent: false,
  };

  const asNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const dollars = (cents) => Number((asNumber(cents) / 100).toFixed(2));

  const normalizeItem = (item, quantityOverride) => {
    if (!item) return null;
    const productId = item.item_id || item.product_id || item.product?.id;
    const variantId = item.variant_id || item.id || item.variant?.id;
    const quantity = quantityOverride ?? item.quantity ?? 1;
    const priceCents = item.price_cents ?? item.final_price ?? item.price ?? item.variant?.price ?? 0;

    return {
      item_id: String(productId || variantId || ''),
      item_name: item.item_name || item.product_title || item.product?.title || item.title || '',
      item_brand: item.item_brand || item.vendor || '',
      item_category: item.item_category || item.product_type || item.product?.type || '',
      item_variant: item.item_variant || item.variant_title || item.variant?.title || '',
      item_variant_id: String(variantId || ''),
      price: dollars(priceCents),
      quantity: asNumber(quantity) || 1,
    };
  };

  const sendEvent = (name, parameters = {}) => {
    window.gtag('event', name, {
      currency: context.currency || 'USD',
      event_source: 'shopify_theme',
      transport_type: 'beacon',
      ...parameters,
    });
  };

  const cartItems = () => (state.cart.items || []).map((item) => normalizeItem(item)).filter(Boolean);
  const cartValue = () => dollars(state.cart.value_cents ?? state.cart.total_price ?? 0);

  const refreshCart = () =>
    fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('cart fetch failed'))))
      .then((cart) => {
        state.cart = {
          value_cents: cart.total_price,
          item_count: cart.item_count,
          items: cart.items || [],
        };
        return state.cart;
      })
      .catch(() => null);

  const sendCartDelta = (previousCart, nextCart) => {
    const previousByVariant = new Map(
      (previousCart?.items || []).map((item) => [String(item.variant_id || item.id || item.variant?.id), item])
    );
    const nextByVariant = new Map(
      (nextCart?.items || []).map((item) => [String(item.variant_id || item.id || item.variant?.id), item])
    );
    const variantIds = new Set([...previousByVariant.keys(), ...nextByVariant.keys()]);

    variantIds.forEach((variantId) => {
      const previousItem = previousByVariant.get(variantId);
      const nextItem = nextByVariant.get(variantId);
      const previousQuantity = asNumber(previousItem?.quantity);
      const nextQuantity = asNumber(nextItem?.quantity);
      const delta = nextQuantity - previousQuantity;
      if (!delta) return;

      const eventItem = normalizeItem(nextItem || previousItem, Math.abs(delta));
      sendEvent(delta > 0 ? 'add_to_cart' : 'remove_from_cart', {
        value: Number((eventItem.price * eventItem.quantity).toFixed(2)),
        items: [eventItem],
      });
    });
  };

  const subscribeToCart = () => {
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS !== 'object' ||
      !PUB_SUB_EVENTS.cartUpdate
    ) {
      window.setTimeout(subscribeToCart, 100);
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const previousCart = state.cart;

      if (event?.source === 'product-form' && event.cartData) {
        const added = event.cartData.items?.[0] || event.cartData;
        const item = normalizeItem(added);
        if (item) {
          sendEvent('add_to_cart', {
            value: Number((item.price * item.quantity).toFixed(2)),
            items: [item],
          });
        }
        refreshCart();
        return;
      }

      refreshCart().then((nextCart) => {
        if (nextCart) sendCartDelta(previousCart, nextCart);
      });
    });
  };

  const trackPlainProductForm = (form) => {
    if (form.closest('product-form')) return;
    const productNode = form.closest('[data-ga4-product]');
    if (!productNode) return;

    const formData = new FormData(form);
    const item = normalizeItem(
      {
        item_id: productNode.dataset.ga4ProductId,
        item_name: productNode.dataset.ga4ProductName,
        item_brand: productNode.dataset.ga4ProductBrand,
        item_category: productNode.dataset.ga4ProductCategory,
        variant_id: formData.get('id') || productNode.dataset.ga4VariantId,
        item_variant: productNode.dataset.ga4VariantName,
        price_cents: productNode.dataset.ga4PriceCents,
      },
      formData.get('quantity') || 1
    );

    if (item) {
      sendEvent('add_to_cart', {
        value: Number((item.price * item.quantity).toFixed(2)),
        items: [item],
      });
    }
  };

  const trackLeadForm = (form) => {
    if (!form.matches('form[action*="/contact"]')) return;
    const contactTags = form.querySelector('[name="contact[tags]"]')?.value || '';
    const isNewsletter = contactTags.toLowerCase().includes('newsletter') || form.classList.contains('newsletter-form');
    sendEvent('generate_lead', {
      lead_type: isNewsletter ? 'newsletter' : 'contact',
      method: 'shopify_form',
    });
  };

  const sendBeginCheckout = () => {
    if (state.checkoutSent || !state.cart.item_count) return;
    state.checkoutSent = true;
    sendEvent('begin_checkout', {
      value: cartValue(),
      items: cartItems(),
    });
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';

      if (action.includes('/cart/add')) trackPlainProductForm(form);
      if (form.querySelector('[name="checkout"]')) sendBeginCheckout();
      trackLeadForm(form);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const checkoutTarget = event.target.closest(
        '[name="checkout"], a[href*="/checkout"], button[data-checkout], .cart__checkout-button'
      );
      if (checkoutTarget) sendBeginCheckout();
    },
    true
  );

  if (context.product) {
    const item = normalizeItem(context.product);
    sendEvent('view_item', {
      value: item.price,
      items: [item],
    });
  }

  if (context.page_type === 'cart' && state.cart.item_count) {
    sendEvent('view_cart', {
      value: cartValue(),
      items: cartItems(),
    });
  }

  subscribeToCart();
})();
